/* ──────────────────────────────────────────────────────────────
   ClientesPage – CRUD de usuarios-cliente
   Adaptado para:
     · Teléfono obligatorio
     · Enlace HubSpot obligatorio
     · Nuevo endpoint /clientes
   ────────────────────────────────────────────────────────────── */

import { useEffect, useState } from 'react';
import {
  Stack,
  Button,
  Paper,
  IconButton,
  Tooltip,
  Link,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  TextField,
  InputAdornment,
  Switch,
  Tabs,
  Tab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import LinkIcon from '@mui/icons-material/Link';
import { useTranslation } from 'react-i18next';

import ClienteDialog from '../../../components/ClienteDialog';
import MotivoDesactivacionDialog from '../../../components/MotivoDesactivacionDialog';
import {
  useClientes,
  useUpdateClienteConMotivo,
  Cliente,
} from '../../../hooks/useClientes';
import type { MotivoDesactivacion } from '../../../types/usuarios';

export default function ClientesPage() {
  const { t } = useTranslation<'translation'>();

  /* ───── hook backend ───── */
  const { rows, loading, refresh, create, update, remove, toggle } = useClientes();
  const updateConMotivo = useUpdateClienteConMotivo();

  /* ───── estado UI ───── */
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Cliente | undefined>();
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<'activos' | 'inactivos'>('activos');

  // Estado para el diálogo de motivo de desactivación
  const [motivoDialogOpen, setMotivoDialogOpen] = useState(false);
  const [clienteADesactivar, setClienteADesactivar] = useState<Cliente | null>(null);

  /* React Query carga automáticamente, no necesitamos refresh manual */

  /* helpers abrir diálogos */
  const openNew = () => {
    setEditing(undefined);
    setDialogOpen(true);
  };
  const openEdit = (c: Cliente) => {
    setEditing(c);
    setDialogOpen(true);
  };

  /* Manejo del toggle con diálogo de motivo */
  const handleToggle = (cliente: Cliente, nuevoEstado: boolean) => {
    if (!nuevoEstado && cliente.activo) {
      // Intentando desactivar → pedir motivo
      setClienteADesactivar(cliente);
      setMotivoDialogOpen(true);
    } else if (nuevoEstado && !cliente.activo) {
      // Reactivar → no requiere motivo
      updateConMotivo.mutate({
        id: cliente.id,
        activo: true,
      });
    }
  };

  const handleConfirmDesactivacion = (motivo: MotivoDesactivacion) => {
    if (!clienteADesactivar) return;

    updateConMotivo.mutate(
      {
        id: clienteADesactivar.id,
        activo: false,
        deactivationReason: motivo,
      },
      {
        onSuccess: () => {
          setMotivoDialogOpen(false);
          setClienteADesactivar(null);
        },
        onError: (error) => {
          console.error('Error al desactivar cliente:', error);
          // El diálogo permanece abierto para que el usuario pueda reintentar
        },
      },
    );
  };

  const handleCancelDesactivacion = () => {
    setMotivoDialogOpen(false);
    setClienteADesactivar(null);
  };

  /* filtrado – incluye teléfono y HubSpot */
  const q = search.toLowerCase();
  const filteredByTab = rows.filter((c) => c.activo === (tab === 'activos'));
  const filtered = filteredByTab.filter((c) =>
    [c.nombreCompleto, c.email, c.telefono, c.hubspotUrl].some((f) =>
      (f ?? '').toLowerCase().includes(q),
    ),
  );
  const contador = filtered.length;

  /* ───────────────── UI ───────────────── */
  return (
    <Stack spacing={2}>
      {/* contador de clientes (según pestaña) */}
      <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{contador}</span>
          <span>clientes {tab === 'activos' ? 'activos' : 'inactivos'}</span>
        </Stack>
      </Paper>

      {/* pestañas activos/inactivos */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        textColor="primary"
        indicatorColor="primary"
      >
        <Tab label="Activos" value="activos" />
        <Tab label="Inactivos" value="inactivos" />
      </Tabs>

      {/* barra superior */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button startIcon={<AddIcon />} variant="contained" onClick={openNew}>
          {t('clients.button.new')}
        </Button>

        <TextField
          placeholder={t<string>('clients.search.placeholder')}
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ maxWidth: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {/* tabla */}
      <Paper>
        {loading ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress />
          </Stack>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('clients.table.id')}</TableCell>
                <TableCell>{t('clients.table.name')}</TableCell>
                <TableCell>E-mail</TableCell>
                <TableCell>{t('clients.table.phone')}</TableCell>
                <TableCell>{t('clients.table.hubspot')}</TableCell>
                <TableCell>{t('clients.table.active')}</TableCell>
                <TableCell width={90} />
              </TableRow>
            </TableHead>

            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{c.id}</TableCell>
                  <TableCell>{c.nombreCompleto}</TableCell>
                  <TableCell>{c.email}</TableCell>
                  <TableCell>{c.telefono}</TableCell>

                  {/* Enlace HubSpot */}
                  <TableCell>
                    <Link
                      href={c.hubspotUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                    >
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <LinkIcon fontSize="inherit" />
                        <span>HubSpot</span>
                      </Stack>
                    </Link>
                  </TableCell>

                  {/* toggle activo/inactivo */}
                  <TableCell>
                    <Switch
                      size="small"
                      checked={c.activo}
                      onChange={(_, v) => handleToggle(c, v)}
                    />
                  </TableCell>

                  {/* acciones */}
                  <TableCell align="right">
                    <Tooltip title={t('common.actions.edit')}>
                      <IconButton size="small" onClick={() => openEdit(c)}>
                        <EditIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={t('common.actions.delete')}>
                      <IconButton size="small" onClick={() => remove(c.id)}>
                        <DeleteIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

              {!filtered.length && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {t('clients.table.noResults')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* diálogo alta / edición */}
      <ClienteDialog
        open={dialogOpen}
        initial={editing}
        onClose={() => setDialogOpen(false)}
        onSave={async (data) => {
          if (editing) {
            await update(editing.id, data);
          } else {
            await create(data);
          }
          setDialogOpen(false);
        }}
        onDelete={
          editing
            ? async () => {
                await remove(editing.id);
                setDialogOpen(false);
              }
            : undefined
        }
      />

      {/* diálogo de motivo de desactivación */}
      <MotivoDesactivacionDialog
        open={motivoDialogOpen}
        onConfirm={handleConfirmDesactivacion}
        onClose={handleCancelDesactivacion}
        clienteNombre={clienteADesactivar?.nombreCompleto}
      />
    </Stack>
  );
}
